import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Plus,
  TrendingUp,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { Empty, Skeleton } from '../components/ui/Empty'
import { Input, Select } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { cn } from '../lib/utils'

// ============================================================
// Types (mirror @squadron/core shapes — kept local to avoid bringing
// the whole core types graph into the webui bundle)
// ============================================================

type CommitmentCategory =
  | 'maintenance'
  | 'retainer'
  | 'license_resale'
  | 'hosting_resale'
  | 'sla'
  | 'other'

type CommitmentStatus = 'active' | 'paused' | 'closed'

type OccurrenceStatus = 'upcoming' | 'invoiced' | 'paid' | 'overdue' | 'skipped' | 'waived'

type RecurrenceKind = 'annual' | 'semiannual' | 'quarterly' | 'monthly' | 'custom'

type ClientCommitment = {
  id: string
  clientId: string
  projectId: string | null
  title: string
  description: string | null
  category: CommitmentCategory
  amount: number
  currency: string
  vatApplicable: boolean
  paymentTerms: string | null
  recurrenceKind: RecurrenceKind
  recurrenceCustomDays: number | null
  firstDueDate: string
  nextDueDate: string
  reminderDaysBefore: number[]
  notifyClientDaysBefore: number | null
  autoRenew: boolean
  autoInvoice: boolean
  status: CommitmentStatus
  closedAt: string | null
  closedReason: string | null
}

type Occurrence = {
  id: string
  commitmentId: string
  dueDate: string
  status: OccurrenceStatus
  invoicedAt: string | null
  invoiceId: string | null
  paidAt: string | null
  paidAmount: number | null
  notes: string | null
}

type ClientMini = { id: string; name: string }
type ProjectMini = { id: string; name: string }

const CATEGORY_LABELS: Record<CommitmentCategory, string> = {
  maintenance: 'Manutenzione',
  retainer: 'Retainer',
  license_resale: 'Licenza rivenduta',
  hosting_resale: 'Hosting rivenduto',
  sla: 'SLA / Supporto',
  other: 'Altro',
}

const RECURRENCE_LABELS: Record<RecurrenceKind, string> = {
  annual: 'Annuale',
  semiannual: 'Semestrale',
  quarterly: 'Trimestrale',
  monthly: 'Mensile',
  custom: 'Custom',
}

function formatEUR(n: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency }).format(n)
}

function daysUntil(isoDate: string): number {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const target = new Date(isoDate)
  target.setHours(0, 0, 0, 0)
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
}

function urgencyClass(days: number): { label: string; cls: string; icon: typeof Clock } {
  if (days < 0)
    return {
      label: `Overdue ${Math.abs(days)}gg`,
      cls: 'text-red-400 bg-red-500/10 border-red-400/30',
      icon: AlertCircle,
    }
  if (days <= 14)
    return {
      label: `${days}gg`,
      cls: 'text-amber-300 bg-amber-500/10 border-amber-400/30',
      icon: Clock,
    }
  return {
    label: `${days}gg`,
    cls: 'text-emerald-300 bg-emerald-500/10 border-emerald-400/30',
    icon: Clock,
  }
}

// ============================================================
// Page shell — 3 tab: Lista, Calendario, Stats
// ============================================================

export function ClientCommitmentsPage() {
  const [tab, setTab] = useState<'list' | 'calendar' | 'stats'>('list')
  const [showNewModal, setShowNewModal] = useState(false)

  return (
    <AppLayout>
      <div className="flex items-start justify-between gap-4 mb-2">
        <PageHeader
          title="Scadenziario"
          description="Manutenzioni, retainer, licenze e contratti ricorrenti dei clienti"
        />
        <Button onClick={() => setShowNewModal(true)} className="shrink-0 mt-1">
          <Plus className="size-4" />
          Nuovo impegno
        </Button>
      </div>

      <div className="mb-4 flex gap-1 border-b border-zinc-800">
        <TabButton active={tab === 'list'} onClick={() => setTab('list')}>
          <CalendarClock className="size-4" /> Lista
        </TabButton>
        <TabButton active={tab === 'calendar'} onClick={() => setTab('calendar')}>
          <Clock className="size-4" /> Calendario
        </TabButton>
        <TabButton active={tab === 'stats'} onClick={() => setTab('stats')}>
          <TrendingUp className="size-4" /> Stats
        </TabButton>
      </div>

      {tab === 'list' && <ListView />}
      {tab === 'calendar' && <CalendarView />}
      {tab === 'stats' && <StatsView />}

      <NewCommitmentDialog open={showNewModal} onClose={() => setShowNewModal(false)} />
    </AppLayout>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-sm font-medium transition border-b-2 -mb-px',
        active
          ? 'text-cyan-300 border-cyan-400'
          : 'text-zinc-400 border-transparent hover:text-zinc-200',
      )}
    >
      {children}
    </button>
  )
}

// ============================================================
// Tab 1 — Lista
// ============================================================

function ListView() {
  const [clientFilter, setClientFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<CommitmentCategory | ''>('')
  const [statusFilter, setStatusFilter] = useState<CommitmentStatus | 'all'>('active')

  const params = new URLSearchParams()
  if (clientFilter) params.set('clientId', clientFilter)
  if (categoryFilter) params.set('category', categoryFilter)
  if (statusFilter) params.set('status', statusFilter)

  const { data, isLoading } = useQuery({
    queryKey: ['client-commitments', clientFilter, categoryFilter, statusFilter],
    queryFn: () =>
      apiClient.get<{ results: ClientCommitment[]; total: number }>(
        `/client-commitments?${params}`,
      ),
  })

  const clientsQuery = useQuery({
    queryKey: ['clients', 'mini'],
    queryFn: () => apiClient.get<{ results: ClientMini[] }>('/clients?limit=200'),
  })

  const clientNameById = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of clientsQuery.data?.results ?? []) m.set(c.id, c.name)
    return m
  }, [clientsQuery.data])

  return (
    <>
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Select
          value={clientFilter}
          onChange={(e) => setClientFilter(e.currentTarget.value)}
          className="min-w-[180px]"
        >
          <option value="">Tutti i clienti</option>
          {clientsQuery.data?.results.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.currentTarget.value as CommitmentCategory | '')}
        >
          <option value="">Tutte le categorie</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.currentTarget.value as CommitmentStatus | 'all')}
        >
          <option value="active">Solo attivi</option>
          <option value="paused">Pausati</option>
          <option value="closed">Chiusi</option>
          <option value="all">Tutti</option>
        </Select>
      </div>

      <Card>
        <CardBody>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : data && data.results.length > 0 ? (
            <ul className="space-y-2">
              {data.results.map((c) => (
                <CommitmentRow
                  key={c.id}
                  commitment={c}
                  clientName={clientNameById.get(c.clientId) ?? '—'}
                />
              ))}
            </ul>
          ) : (
            <Empty
              icon={CalendarClock}
              title="Nessun impegno"
              description="Crea il primo impegno ricorrente cliente con il pulsante 'Nuovo impegno'."
            />
          )}
        </CardBody>
      </Card>
    </>
  )
}

function CommitmentRow({
  commitment,
  clientName,
}: {
  commitment: ClientCommitment
  clientName: string
}) {
  const [showDetail, setShowDetail] = useState(false)
  const days = daysUntil(commitment.nextDueDate)
  const urgency = urgencyClass(days)
  const UrgencyIcon = urgency.icon

  return (
    <>
      <li className="list-none">
        <button
          type="button"
          onClick={() => setShowDetail(true)}
          className="w-full grid grid-cols-[1fr_auto] gap-3 items-center p-3 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 transition cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-cyan-400/40"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-sm font-semibold text-zinc-100 truncate">{commitment.title}</div>
              <Badge variant="default">{CATEGORY_LABELS[commitment.category]}</Badge>
              {commitment.status !== 'active' && (
                <Badge variant="default">{commitment.status}</Badge>
              )}
            </div>
            <div className="text-xs text-zinc-400">
              {clientName} · {RECURRENCE_LABELS[commitment.recurrenceKind]}
              {commitment.autoRenew && ' · auto-renew'}
              {commitment.autoInvoice && ' · auto-invoice'}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-semibold text-zinc-100 tabular-nums">
              {formatEUR(commitment.amount, commitment.currency)}
            </div>
            <div
              className={cn(
                'mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border',
                urgency.cls,
              )}
            >
              <UrgencyIcon className="size-3" />
              {urgency.label}
            </div>
          </div>
        </button>
      </li>
      <CommitmentDetailDrawer
        open={showDetail}
        onClose={() => setShowDetail(false)}
        commitmentId={commitment.id}
      />
    </>
  )
}

// ============================================================
// Detail drawer — shows occurrences + actions (markPaid, skip)
// ============================================================

function CommitmentDetailDrawer({
  open,
  onClose,
  commitmentId,
}: {
  open: boolean
  onClose: () => void
  commitmentId: string
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    enabled: open,
    queryKey: ['client-commitment', commitmentId],
    queryFn: () =>
      apiClient.get<{ commitment: ClientCommitment; occurrences: Occurrence[] }>(
        `/client-commitments/${commitmentId}?includeOccurrences=true`,
      ),
  })

  const markPaidMutation = useMutation({
    mutationFn: (occurrenceId: string) =>
      apiClient.post(`/client-commitments/occurrences/${occurrenceId}/pay`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-commitments'] })
      qc.invalidateQueries({ queryKey: ['client-commitment', commitmentId] })
    },
  })

  const skipMutation = useMutation({
    mutationFn: (args: { occurrenceId: string; reason: string; waive: boolean }) =>
      apiClient.post(`/client-commitments/occurrences/${args.occurrenceId}/skip`, {
        reason: args.reason,
        waive: args.waive,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-commitments'] })
      qc.invalidateQueries({ queryKey: ['client-commitment', commitmentId] })
    },
  })

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={data?.commitment.title ?? 'Caricamento…'}
      description={data ? `${CATEGORY_LABELS[data.commitment.category]} · ${RECURRENCE_LABELS[data.commitment.recurrenceKind]}` : undefined}
      size="lg"
    >
      <div className="p-5">
        {isLoading || !data ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
              <Info label="Importo" value={formatEUR(data.commitment.amount, data.commitment.currency)} />
              <Info label="IVA" value={data.commitment.vatApplicable ? 'Applicabile' : 'Esente'} />
              <Info label="Prossima scadenza" value={new Date(data.commitment.nextDueDate).toLocaleDateString('it-IT')} />
              <Info label="Auto-renew" value={data.commitment.autoRenew ? 'Sì' : 'No'} />
              <Info label="Payment terms" value={data.commitment.paymentTerms ?? '—'} />
              <Info label="Reminder days" value={data.commitment.reminderDaysBefore.join(', ')} />
            </div>

            {data.commitment.description && (
              <div className="mb-5 p-3 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-300">
                {data.commitment.description}
              </div>
            )}

            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-2">
              Storico occorrenze
            </h3>
            {data.occurrences.length === 0 ? (
              <div className="text-sm text-zinc-500">Nessuna occorrenza ancora.</div>
            ) : (
              <ul className="space-y-1.5">
                {data.occurrences.map((occ) => (
                  <li
                    key={occ.id}
                    className="flex items-center justify-between gap-3 p-2.5 rounded bg-zinc-900/60 border border-zinc-800"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <OccurrenceStatusIcon status={occ.status} />
                      <span className="text-sm text-zinc-200">
                        {new Date(occ.dueDate).toLocaleDateString('it-IT')}
                      </span>
                      <Badge variant="default">{occ.status}</Badge>
                      {occ.paidAmount && (
                        <span className="text-xs text-emerald-400">
                          {formatEUR(occ.paidAmount, data.commitment.currency)}
                        </span>
                      )}
                    </div>
                    {(occ.status === 'upcoming' || occ.status === 'overdue' || occ.status === 'invoiced') && (
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          onClick={() => markPaidMutation.mutate(occ.id)}
                          disabled={markPaidMutation.isPending}
                          className="text-xs px-2 py-1"
                        >
                          <CheckCircle2 className="size-3" />
                          Pagata
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            const reason = window.prompt('Motivo skip:')
                            if (reason)
                              skipMutation.mutate({ occurrenceId: occ.id, reason, waive: false })
                          }}
                          disabled={skipMutation.isPending}
                          className="text-xs px-2 py-1"
                        >
                          <XCircle className="size-3" />
                          Salta
                        </Button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-0.5">
        {label}
      </div>
      <div className="text-sm text-zinc-200">{value}</div>
    </div>
  )
}

function OccurrenceStatusIcon({ status }: { status: OccurrenceStatus }) {
  const color =
    status === 'paid'
      ? 'text-emerald-400'
      : status === 'invoiced'
        ? 'text-cyan-400'
        : status === 'overdue'
          ? 'text-red-400'
          : status === 'skipped' || status === 'waived'
            ? 'text-zinc-500'
            : 'text-amber-300'
  return <Clock className={cn('size-3.5', color)} />
}

// ============================================================
// Tab 2 — Calendar (simple month grid)
// ============================================================

function CalendarView() {
  const { data, isLoading } = useQuery({
    queryKey: ['client-commitments', 'upcoming-occurrences'],
    queryFn: () =>
      apiClient.get<{ results: Occurrence[] }>(
        '/client-commitments/occurrences/upcoming?daysHorizon=180&includeOverdue=true',
      ),
  })

  const byDate = useMemo(() => {
    const m = new Map<string, Occurrence[]>()
    for (const o of data?.results ?? []) {
      const key = o.dueDate.slice(0, 10)
      const list = m.get(key) ?? []
      list.push(o)
      m.set(key, list)
    }
    return m
  }, [data])

  const monthsToShow = 3
  const today = new Date()

  return (
    <Card>
      <CardBody>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : (
          <div className="space-y-6">
            {Array.from({ length: monthsToShow }).map((_, monthOffset) => (
              <MonthGrid
                // biome-ignore lint/suspicious/noArrayIndexKey: stable month sequence
                key={monthOffset}
                year={today.getFullYear()}
                month={today.getMonth() + monthOffset}
                occurrencesByDate={byDate}
              />
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function MonthGrid({
  year,
  month,
  occurrencesByDate,
}: {
  year: number
  month: number
  occurrencesByDate: Map<string, Occurrence[]>
}) {
  const firstDay = new Date(year, month, 1)
  const monthLabel = firstDay.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  // Monday-first week: getDay() returns 0=Sun..6=Sat, normalize to 0=Mon..6=Sun
  const firstWeekday = (firstDay.getDay() + 6) % 7

  const cells: Array<{ day: number; occ: Occurrence[] } | null> = []
  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ day: d, occ: occurrencesByDate.get(key) ?? [] })
  }

  const todayIso = new Date().toISOString().slice(0, 10)

  return (
    <div>
      <h3 className="text-sm font-bold text-zinc-100 mb-2 capitalize">{monthLabel}</h3>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
        {['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'].map((d) => (
          <div key={d} className="text-center font-semibold">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, idx) => {
          if (!cell)
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: empty leading cells
              <div key={`empty-${idx}`} className="aspect-square" />
            )
          const cellIso = `${year}-${String(month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`
          const isToday = cellIso === todayIso
          const hasOcc = cell.occ.length > 0
          return (
            <div
              key={cell.day}
              className={cn(
                'aspect-square p-1 rounded text-xs flex flex-col border',
                isToday ? 'border-cyan-400 bg-cyan-500/5' : 'border-zinc-800',
                hasOcc && !isToday && 'bg-zinc-900/60',
              )}
              title={cell.occ.map((o) => o.status).join(', ')}
            >
              <div
                className={cn(
                  'font-semibold',
                  isToday ? 'text-cyan-300' : hasOcc ? 'text-zinc-100' : 'text-zinc-500',
                )}
              >
                {cell.day}
              </div>
              {hasOcc && (
                <div className="mt-auto flex flex-wrap gap-0.5">
                  {cell.occ.slice(0, 3).map((o) => (
                    <span
                      key={o.id}
                      className={cn(
                        'inline-block size-1.5 rounded-full',
                        o.status === 'overdue'
                          ? 'bg-red-400'
                          : o.status === 'invoiced'
                            ? 'bg-cyan-400'
                            : 'bg-amber-300',
                      )}
                    />
                  ))}
                  {cell.occ.length > 3 && (
                    <span className="text-[9px] text-zinc-500">+{cell.occ.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================
// Tab 3 — Stats (forecast cashflow)
// ============================================================

function StatsView() {
  const forecast30 = useQuery({
    queryKey: ['client-commitments', 'forecast', 30],
    queryFn: () =>
      apiClient.get<{ totalAmount: number; commitmentCount: number; occurrenceCount: number }>(
        '/client-commitments/forecast?days=30',
      ),
  })
  const forecast90 = useQuery({
    queryKey: ['client-commitments', 'forecast', 90],
    queryFn: () =>
      apiClient.get<{ totalAmount: number; commitmentCount: number; occurrenceCount: number }>(
        '/client-commitments/forecast?days=90',
      ),
  })
  const forecast365 = useQuery({
    queryKey: ['client-commitments', 'forecast', 365],
    queryFn: () =>
      apiClient.get<{ totalAmount: number; commitmentCount: number; occurrenceCount: number }>(
        '/client-commitments/forecast?days=365',
      ),
  })

  return (
    <div className="grid gap-4 md:grid-cols-3">
      <StatCard label="Prossimi 30 giorni" data={forecast30.data} loading={forecast30.isLoading} />
      <StatCard label="Prossimi 90 giorni" data={forecast90.data} loading={forecast90.isLoading} />
      <StatCard
        label="Prossimi 12 mesi"
        data={forecast365.data}
        loading={forecast365.isLoading}
        highlight
      />
    </div>
  )
}

function StatCard({
  label,
  data,
  loading,
  highlight,
}: {
  label: string
  data?: { totalAmount: number; commitmentCount: number; occurrenceCount: number }
  loading: boolean
  highlight?: boolean
}) {
  return (
    <Card>
      <CardBody>
        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">
          {label}
        </div>
        {loading ? (
          <Skeleton className="h-12 w-full" />
        ) : (
          <>
            <div
              className={cn(
                'text-3xl font-bold tabular-nums mb-2',
                highlight ? 'text-cyan-300' : 'text-zinc-100',
              )}
            >
              {data ? formatEUR(data.totalAmount) : '—'}
            </div>
            <div className="text-xs text-zinc-400">
              {data?.commitmentCount ?? 0} impegni · {data?.occurrenceCount ?? 0} scadenze
            </div>
          </>
        )}
      </CardBody>
    </Card>
  )
}

// ============================================================
// New / Edit commitment dialog
// ============================================================

function NewCommitmentDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    clientId: '',
    projectId: '',
    title: '',
    description: '',
    category: 'maintenance' as CommitmentCategory,
    amount: '' as string,
    currency: 'EUR',
    vatApplicable: true,
    paymentTerms: '',
    recurrenceKind: 'annual' as RecurrenceKind,
    recurrenceCustomDays: 30,
    firstDueDate: new Date(new Date().setMonth(new Date().getMonth() + 1))
      .toISOString()
      .slice(0, 10),
    autoRenew: true,
  })

  const clientsQuery = useQuery({
    enabled: open,
    queryKey: ['clients', 'mini'],
    queryFn: () => apiClient.get<{ results: ClientMini[] }>('/clients?limit=200'),
  })

  const projectsQuery = useQuery({
    enabled: open && !!form.clientId,
    queryKey: ['projects', form.clientId],
    queryFn: () =>
      apiClient.get<{ results: ProjectMini[] }>(`/projects?clientId=${form.clientId}&limit=200`),
  })

  const mutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post('/client-commitments', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client-commitments'] })
      onClose()
      setForm({
        ...form,
        clientId: '',
        projectId: '',
        title: '',
        description: '',
        amount: '',
        paymentTerms: '',
      })
    },
  })

  const submit = () => {
    if (!form.clientId || !form.title || !form.amount || !form.firstDueDate) return
    const body: Record<string, unknown> = {
      clientId: form.clientId,
      title: form.title,
      category: form.category,
      amount: Number(form.amount),
      currency: form.currency,
      vatApplicable: form.vatApplicable,
      recurrenceKind: form.recurrenceKind,
      firstDueDate: form.firstDueDate,
      autoRenew: form.autoRenew,
    }
    if (form.projectId) body.projectId = form.projectId
    if (form.description) body.description = form.description
    if (form.paymentTerms) body.paymentTerms = form.paymentTerms
    if (form.recurrenceKind === 'custom') body.recurrenceCustomDays = form.recurrenceCustomDays
    mutation.mutate(body)
  }

  return (
    <Dialog open={open} onClose={onClose} title="Nuovo impegno ricorrente cliente" size="lg">
      <div className="p-5 space-y-3">
        <Field label="Cliente *">
          <Select
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.currentTarget.value, projectId: '' })}
            className="w-full"
          >
            <option value="">Seleziona cliente…</option>
            {clientsQuery.data?.results.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>

        {form.clientId && (
          <Field label="Progetto (opzionale)">
            <Select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.currentTarget.value })}
              className="w-full"
            >
              <option value="">Nessun progetto specifico</option>
              {projectsQuery.data?.results.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Titolo *">
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.currentTarget.value })}
            placeholder="es. Manutenzione annua BikeFit Pro"
            className="w-full"
          />
        </Field>

        <Field label="Descrizione (opzionale)">
          <Input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.currentTarget.value })}
            placeholder="Note aggiuntive sull'impegno"
            className="w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Categoria *">
            <Select
              value={form.category}
              onChange={(e) =>
                setForm({ ...form, category: e.currentTarget.value as CommitmentCategory })
              }
              className="w-full"
            >
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Importo *">
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.currentTarget.value })}
                placeholder="0.00"
                className="flex-1"
              />
              <Select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.currentTarget.value })}
                className="w-24"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </Select>
            </div>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Ricorrenza *">
            <Select
              value={form.recurrenceKind}
              onChange={(e) =>
                setForm({ ...form, recurrenceKind: e.currentTarget.value as RecurrenceKind })
              }
              className="w-full"
            >
              {Object.entries(RECURRENCE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Prima scadenza *">
            <Input
              type="date"
              value={form.firstDueDate}
              onChange={(e) => setForm({ ...form, firstDueDate: e.currentTarget.value })}
              className="w-full"
            />
          </Field>
        </div>

        {form.recurrenceKind === 'custom' && (
          <Field label="Giorni custom *">
            <Input
              type="number"
              min="1"
              value={form.recurrenceCustomDays}
              onChange={(e) =>
                setForm({ ...form, recurrenceCustomDays: Number(e.currentTarget.value) })
              }
              className="w-32"
            />
          </Field>
        )}

        <Field label="Payment terms (opzionale)">
          <Input
            value={form.paymentTerms}
            onChange={(e) => setForm({ ...form, paymentTerms: e.currentTarget.value })}
            placeholder="es. Bonifico 30gg DF"
            className="w-full"
          />
        </Field>

        <div className="flex gap-4 items-center pt-2">
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={form.vatApplicable}
              onChange={(e) => setForm({ ...form, vatApplicable: e.target.checked })}
            />
            IVA applicabile
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input
              type="checkbox"
              checked={form.autoRenew}
              onChange={(e) => setForm({ ...form, autoRenew: e.target.checked })}
            />
            Auto-renew al pagamento
          </label>
        </div>

        {mutation.isError && (
          <div className="text-sm text-red-400">
            Errore:{' '}
            {mutation.error instanceof Error ? mutation.error.message : 'Salvataggio fallito'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={
              !form.clientId ||
              !form.title ||
              !form.amount ||
              !form.firstDueDate ||
              mutation.isPending
            }
          >
            <Plus className="size-4" />
            {mutation.isPending ? 'Salvataggio…' : 'Crea impegno'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium text-zinc-400 mb-1.5">{label}</div>
      {children}
    </div>
  )
}
